'use client'

/**
 * NextJumuahWidget (web) — "Next Jumah Near You" on the directory, mirroring the mobile Discover card.
 * Up to 3 nearby upcoming Jumu'ah jamaats (real committee times only; a masjid's multiple slots are
 * expanded server-side). Self-time-boxed (Thu → pre-Friday-Asr) and self-empty-CTA — renders nothing
 * outside the window and never fabricates a time. Reuses /api/jumuah/nearby.
 */

import { useEffect, useState, type CSSProperties } from 'react'
import { Landmark, ChevronRight, MapPin } from 'lucide-react'

interface Jamaat { masjidId: string; slug: string; name: string; city: string | null; distanceKm: number; time24: string; timeLabel: string }

function parseHm(s: string | undefined | null): number | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(s || ''))
  if (!m) return null
  const h = Number(m[1]); const min = Number(m[2])
  return Number.isFinite(h) && Number.isFinite(min) ? h * 60 + min : null
}
function formatDistance(km: number): string {
  return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`
}

export function NextJumuahWidget({ city }: { city?: string }) {
  const now = new Date()
  const day = now.getDay() // 4 Thu, 5 Fri
  const nowMin = now.getHours() * 60 + now.getMinutes()
  const isThursday = day === 4
  const isFriday = day === 5
  const inDayWindow = isThursday || isFriday

  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null)
  const [coordsChecked, setCoordsChecked] = useState(false)
  useEffect(() => {
    if (!inDayWindow || typeof navigator === 'undefined' || !navigator.geolocation) { setCoordsChecked(true); return }
    let alive = true
    navigator.geolocation.getCurrentPosition(
      (pos) => { if (alive) { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setCoordsChecked(true) } },
      () => { if (alive) setCoordsChecked(true) },
      { timeout: 8000, maximumAge: 300000 },
    )
    return () => { alive = false }
  }, [inDayWindow])

  // Friday only: fetch the user's local Asr — hide once Asr arrives (Jumu'ah is over by Asr).
  const [asrMin, setAsrMin] = useState<number | null>(null)
  const [asrChecked, setAsrChecked] = useState(!isFriday)
  useEffect(() => {
    if (!isFriday) { setAsrChecked(true); return }
    let alive = true
    fetch(`/api/prayer-times?city=${encodeURIComponent(city || 'Hyderabad')}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) { setAsrMin(parseHm(d?.times?.Asr)); setAsrChecked(true) } })
      .catch(() => { if (alive) setAsrChecked(true) })
    return () => { alive = false }
  }, [isFriday, city])

  const pastAsr = isFriday && asrMin != null && nowMin >= asrMin
  const shouldShow = inDayWindow && coordsChecked && !!coords && !pastAsr && asrChecked

  const [jamaats, setJamaats] = useState<Jamaat[] | null>(null)
  useEffect(() => {
    if (!shouldShow || !coords) { setJamaats(null); return }
    let alive = true
    const params = new URLSearchParams({ lat: String(coords.lat), lng: String(coords.lng), limit: '3' })
    if (isFriday) params.set('nowMin', String(nowMin))
    fetch(`/api/jumuah/nearby?${params.toString()}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) setJamaats(Array.isArray(d?.jamaats) ? d.jamaats : []) })
      .catch(() => { if (alive) setJamaats([]) })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShow, coords?.lat, coords?.lng, isFriday])

  // Don't reserve space until we know we're in-window with a location (avoids an empty flash on web,
  // where a location prompt is separate from the honest no-times CTA).
  if (!inDayWindow || (coordsChecked && !coords)) return null
  if (!shouldShow) return null

  const hasItems = !!jamaats && jamaats.length > 0
  const seeAll = () => { document.querySelector('.directory-kit-grid, .directory-list-grid')?.scrollIntoView({ behavior: 'smooth' }) }

  return (
    <div style={S.card}>
      <div style={S.header}>
        <div style={S.headerLeft}>
          <Landmark size={18} color="#F5C451" />
          <span style={S.title}>Next Jumah Near You</span>
        </div>
        <button type="button" onClick={seeAll} style={S.seeAll} aria-label="See all Jumu'ah timings">
          See all <ChevronRight size={15} />
        </button>
      </div>

      {hasItems ? (
        <div style={S.row}>
          {jamaats!.map((j) => (
            <a key={`${j.masjidId}-${j.time24}`} href={`/masjids/${j.slug}`} style={S.jCard}>
              <span style={S.jName}>{j.name}</span>
              <span style={S.jDist}><MapPin size={12} /> {formatDistance(j.distanceKm)}</span>
              <span style={S.jTime}>{j.timeLabel}</span>
            </a>
          ))}
        </div>
      ) : jamaats === null ? (
        <div style={S.loading}>Loading nearby Jumu&apos;ah timings…</div>
      ) : (
        <div style={S.empty}>
          No Jumu&apos;ah timings nearby yet — ask your local masjid to add their Jumu&apos;ah timings on Masjids.in.
        </div>
      )}
    </div>
  )
}

// Inline styles — self-contained so the widget matches the mockup without touching the global sheet.
const S: Record<string, CSSProperties> = {
  card: { background: '#0C2E24', border: '1px solid rgba(45,212,164,0.22)', borderRadius: 16, padding: 16, margin: '0 0 16px', display: 'flex', flexDirection: 'column', gap: 12 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 8 },
  title: { color: '#E8FBF4', fontWeight: 800, fontSize: 16 },
  seeAll: { display: 'inline-flex', alignItems: 'center', gap: 2, background: 'none', border: 'none', color: '#34D399', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  row: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 },
  jCard: { display: 'flex', flexDirection: 'column', gap: 6, background: '#0A241C', border: '1px solid rgba(45,212,164,0.18)', borderRadius: 12, padding: '12px 12px', textDecoration: 'none', minHeight: 84 },
  jName: { color: '#E8FBF4', fontWeight: 700, fontSize: 13, lineHeight: 1.25 },
  jDist: { color: '#94A3B8', fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 3 },
  jTime: { color: '#F5C451', fontWeight: 800, fontSize: 15, marginTop: 'auto' },
  loading: { color: '#94A3B8', fontSize: 13, padding: '8px 0' },
  empty: { color: '#CBD5E1', fontSize: 13, lineHeight: 1.5, background: '#0A241C', border: '1px solid rgba(45,212,164,0.18)', borderRadius: 12, padding: 14 },
}
